import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../../stores/session/sessionStore';
import { Spinner } from '../../ui/Spinner/Spinner';
import { Button } from '../../ui/Button/Button';
import { requestPortalRenonce } from '../../utils/portal/portalRenonce';
import { TranslationKey } from '../../i18n/translationKeys';

/**
 * Full-screen overlay shown while a portal cold-session recovery (re-nonce) is in flight, and when the
 * host could not re-mint a nonce. Makes recovery visible instead of a silent bounce to the index.
 *
 * The failed state offers a retry rather than only an instruction: a host that answered
 * `nonce-refresh-error` once may well answer the next request, and without an action here the overlay
 * covers the whole app with nothing to click.
 */
export const ReconnectingOverlay: React.FC = () => {
    const { t } = useTranslation();
    const reconnecting = useSessionStore((s) => s.reconnecting);
    const failed = useSessionStore((s) => s.connectSessionFailed);
    const setConnectSessionFailed = useSessionStore((s) => s.setConnectSessionFailed);

    if (!reconnecting && !failed) return null;

    function handleRetry(): void {
        // Drop the failed state first: requestPortalRenonce raises the reconnecting overlay, and
        // leaving both set would keep rendering the failure branch over the new attempt.
        setConnectSessionFailed(false);
        requestPortalRenonce();
    }

    return (
        <div
            data-testid={failed ? 'connect-session-failed' : 'connect-reconnecting'}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-surface/90"
        >
            <div className="flex flex-col items-center gap-m px-xl text-center">
                {failed ? (
                    <>
                        <p className="text-s text-primary-700">{t(TranslationKey.Auth.ConnectSessionFailed)}</p>
                        <Button
                            type="button"
                            data-testid="connect-session-failed-retry-btn"
                            onClick={handleRetry}
                        >
                            {t(TranslationKey.Auth.Retry)}
                        </Button>
                    </>
                ) : (
                    <>
                        <Spinner className="h-6 w-6" sparks />
                        <p className="text-s text-primary-700">{t(TranslationKey.Auth.Reconnecting)}</p>
                    </>
                )}
            </div>
        </div>
    );
};
